import React, { useEffect, useRef, useState } from "react";
import { CoordinateSystem } from "./CoordinateSystem";
import { formatLength } from "../../commons/Utils";
import * as Commons from "oc-commons/Commons";

function getNiceScale(length: number): number {
  if (length <= 0) return 0;

  const exponent = Math.floor(Math.log10(length));
  const fraction = length / Math.pow(10, exponent);
  let niceFraction: number;

  if (fraction >= 5) {
    niceFraction = 5;
  } else if (fraction >= 2) {
    niceFraction = 2;
  } else {
    niceFraction = 1;
  }

  return niceFraction * Math.pow(10, exponent);
}

interface ScalebarProps {
  cs: CoordinateSystem;
}

export function Scalebar({ cs }: ScalebarProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [svgSize, setSvgSize] = useState({ width: 300, height: 10 });

  const svgWidthPadding = 20;

  useEffect(() => {
    const updateWidth = () => {
      if (svgRef.current) {
        const r = svgRef.current.getBoundingClientRect();
        setSvgSize({ width: r.width, height: r.height });
      }
    };
    Commons.sleepUntil(() => !!svgRef.current).then(updateWidth);
    window.addEventListener("resize", updateWidth);
    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  // Bestimme den Maßstab basierend auf der aktuellen Zoomstufe
  const lengthInMeter = cs.lengthToGrundriss(svgSize.width - svgWidthPadding * 2);
  const niceMeters = getNiceScale(lengthInMeter);
  const nicePixels = cs.lengthToScreen(niceMeters);

  // Tick-Marken und Beschriftungen
  const tickHeightLong = 10;
  const labelOffset = 15;

  // Tick-Marken Daten (nur Start und Ende)
  const tick = {
    x: svgWidthPadding + nicePixels,
    label: formatLength(niceMeters, 0),
    height: tickHeightLong,
  };

  return (
    <div className="scalebar-container">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        className="no-pointer-events-tree"
        ref={svgRef}
        viewBox={`0 0 ${svgSize.width} ${svgSize.height}`}
      >
        <line
          x1={svgWidthPadding}
          y1={tickHeightLong}
          x2={tick.x}
          y2={tickHeightLong}
          className="scalebar-line"
        />

        <line
          x1={svgWidthPadding}
          y1={tickHeightLong}
          x2={svgWidthPadding}
          y2={0}
          className="scalebar-tick"
        />
        <line x1={tick.x} y1={tickHeightLong} x2={tick.x} y2={0} className="scalebar-tick" />
        <text
          x={tick.x}
          y={tickHeightLong + labelOffset}
          textAnchor="middle"
          className="scalebar-label"
        >
          {tick.label}
        </text>
      </svg>
    </div>
  );
}
